import { DATA_SERVICE_BASE_URL } from '../config/runtime';
import { clearTokens, persistTokens, StoredTokens } from '../auth/tokenStore';

export interface LoginResponse {
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  sessionId?: number | string;
  userId?: number | string;
  requiresMfa?: boolean;
  tempToken?: string;
  firstName?: string;
  displayName?: string;
  linked?: boolean;
}

export interface UserProfile {
  userId?: number | string;
  id?: number | string;
  email?: string;
  firstName?: string;
  lastName?: string;
  authorities?: string[];
  roles?: string[];
  firstLoginAt?: string;
}

// Email/password login. Persists tokens on success.
export async function login(email: string, password: string, rememberMe = true): Promise<LoginResponse> {
  const res = await fetch(`${DATA_SERVICE_BASE_URL}/api/users/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password, rememberMe, product: 'SCOUTCAST' }),
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok) {
    const err = new Error(data?.message || data?.error || 'Login failed') as Error & { status?: number; data?: unknown };
    err.status = res.status;
    err.data = data;
    throw err;
  }
  if (data.requiresMfa) {
    // A temporary MFA credential is not an authenticated session.
    clearTokens();
  } else {
    persistTokens({ ...(data as StoredTokens), rememberMe, sessionMode: 'legacy' });
  }
  return data as LoginResponse;
}

export interface RegisterInput {
  email: string;
  firstName?: string;
  lastName?: string;
}

export interface RegistrationResponse {
  status: string;
  message: string;
}

// Creates an inactive user; a verification email is sent by the backend.
export async function register(input: RegisterInput): Promise<RegistrationResponse> {
  const res = await fetch(`${DATA_SERVICE_BASE_URL}/api/users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ ...input, product: 'SCOUTCAST' }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.message || data?.error || 'Registration failed');
  }
  return data as RegistrationResponse;
}

export async function validateToken(token: string): Promise<UserProfile> {
  const res = await fetch(
    `${DATA_SERVICE_BASE_URL}/api/users/validate-token?token=${encodeURIComponent(token)}`,
    { credentials: 'include' },
  );
  if (!res.ok) throw new Error('Token validation failed');
  return (await res.json()) as UserProfile;
}

export async function logout(accessToken: string): Promise<void> {
  try {
    await fetch(`${DATA_SERVICE_BASE_URL}/api/users/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  } catch {
    /* best effort */
  }
}
